/**
 * Rendering. Pure functions from state to markup — no element is mutated in place, the whole
 * region is re-rendered. At this size that is cheaper to reason about than diffing, and it
 * removes an entire bug class: a stale node from a previous PSBT surviving on screen next to a
 * fresh one.
 */
import { html, raw, esc, btc, short, type Raw } from '@signingroom/ui/dom';
import { reviewPanel, PILL } from '@signingroom/ui/review';
import { state, allAcknowledged, policyMet, activeNetwork } from './state.js';
import { hasWebHid } from './webhid.js';
import {
  explain, diagnoseDescriptor, SEVERITY_PLAIN, NETWORK_PLAIN,
} from '@signingroom/core';
import type { Finding, Severity, Intent, IntentOutput } from '@signingroom/core';

/** Severity → the house stylesheet's ramp. */

/* ── 1. vault ─────────────────────────────────────────────────────────────── */

export function vaultView(_opened: readonly string[] = []): Raw {
  const v = state.vault;
  const p = state.parsed;

  /*
   * Only the FAILURE stays next to the input. The "vault loaded" summary moved to the rail
   * (`vaultRail`), where it stays visible beside the findings that depend on it — the policy
   * and the network are what a reader checks a CHANGE_NOT_MINE against, and they used to
   * scroll away the moment the transaction was loaded.
   */
  /*
   * WHAT THEY PASTED, BEFORE WHAT THE ENGINE SAID ABOUT IT.
   *
   * The old version showed `UNSUPPORTED_DESCRIPTOR: no registered descriptor class matches;
   * refusing to parse on a best-effort basis` and a paragraph about half-understood descriptors.
   * Correct, and a dead end: somebody pastes their single-signature wallet and cannot tell
   * whether they did something wrong, their wallet is wrong, or the product is.
   *
   * `diagnoseDescriptor` reads the SHAPE of the text and says which of those it is, with a next
   * step. The engine's own message stays one disclosure down: it is the precise answer, and an
   * auditor still needs it.
   */
  const diag = state.vaultError ? diagnoseDescriptor(state.vault.descriptor) : null;
  const status = state.vaultError
    ? html`<div class="note ${diag && !diag.danger ? 'f' : 'r'}" id="vault-status">
             <div class="h">${diag ? diag.title : 'This does not look like a wallet description'}</div>
             ${diag
               ? html`<p>${diag.meaning}</p>
                      <p style="margin-top:8px"><strong>What to do:</strong> ${diag.whatToDo}</p>`
               : html`<p>
                        This page reads wallets that one key controls. It could not make sense of
                        what is in the box.
                      </p>
                      <p style="margin-top:8px">
                        <strong>What to do:</strong> export the description again from your wallet
                        software and paste the whole line, including the part after the "#".
                      </p>`}
             ${diag && diag.danger
               ? raw('')
               : html`<details class="tech">
                        <summary>Technical detail</summary>
                        <p class="mono" style="font-size:11.5px;margin-top:8px">${state.vaultError}</p>
                        <p style="margin-top:6px">
                          That is the parser's own reason. It stops at the first thing it cannot
                          read, so it can name a different fault from the one above; fix the one
                          above first.
                        </p>
                        <p style="margin-top:6px">
                          This page reads single-key wallets only —
                          <code class="mono">wpkh(...)</code> and
                          <code class="mono">sh(wpkh(...))</code>. Anything else is refused
                          rather than parsed on a best-effort basis: a half-understood
                          description gives wrong answers about which money is yours, which is
                          the one thing this tool must never do.
                        </p>
                      </details>`}
           </div>`
    : raw('');

  /*
   * Once a vault is applied the form is settled work, so it collapses to one line.
   *
   * Expanded it costs ~470px of the working column — a descriptor textarea, three number
   * inputs and the gap-limit explainer — all of it sitting above the transaction a reader is
   * actually here to look at. It stays a `<details>` rather than being hidden, because changing
   * the scan window is exactly what somebody does after a CHANGE_NOT_MINE, and `open` is driven
   * by whether the vault is settled, not by a click we would then have to remember.
   */
  const settled = !!p && !state.vaultError;

  return html`
    <details class="card disc" id="vault-card">
      <summary>
        <h4 style="margin:0">${state.tab === 'control' ? 'Your wallet' : '1 · Your wallet'}</h4>
        ${settled
          ? html`<span class="pill ok" style="margin-left:8px">${
              p!.n === 1 ? 'One key' : `${p!.m} of ${p!.n} must approve`} · ${
              NETWORK_PLAIN[p!.network].label}</span>`
          : html`<span class="pill info" style="margin-left:8px">not set up yet</span>`}
      </summary>
      ${settled
        ? html`<div class="note" style="margin:12px 0">
                 ${p!.n === 1
                   ? html`<div class="h">This wallet is controlled by a single key</div>
                          One person can move this money on their own, so there is nobody else to
                          catch a mistake before it happens. That makes reading a payment
                          carefully before approving it matter more here, not less.`
                   : html`<div class="h">This wallet needs ${p!.m} of its ${p!.n} keys to approve a payment</div>
                          Any ${p!.m} of the ${p!.n} people holding a key can move money together.
                          Fewer than ${p!.m} cannot, and no single one of them can act alone.`}
                 ${p!.network === 'mainnet'
                   ? html` This wallet holds <strong>real bitcoin</strong>.`
                   : html` This wallet holds <strong>test coins, not real money</strong>.`}
               </div>`
        : html`<p class="sub" style="margin:12px 0 12px">
                 Nothing here is looked up anywhere — this page makes no network calls, so it
                 cannot go and find your wallet. It needs your wallet's own description in order
                 to work out which addresses are yours: that is what lets it prove you control
                 one, and what lets it tell your change apart from a stranger's address when you
                 read a payment.
               </p>`}

      <label class="label" for="descriptor" style="display:block;margin-bottom:6px">
        Your wallet's description
      </label>
      <p class="sub" style="margin:0 0 6px;font-size:12px">
        A line of text your wallet software calls an <em>output descriptor</em>. Sparrow,
        Coldcard, Nunchuk and Bitcoin Core can all export it. It contains no private keys and
        cannot spend anything.
      </p>
      <textarea id="descriptor" class="mono" rows="4" spellcheck="false"
        placeholder="wpkh([f3a1b2c9/84h/0h/0h]xpub.../<0;1>/*)#checksum"
        style="width:100%;font-size:12px;padding:10px;border:1px solid var(--ink);background:var(--card);resize:vertical"
      >${v.descriptor}</textarea>

      ${status}

      <div class="row" style="margin-top:10px">
        <button class="btn ghost sm" id="load-example" type="button">
          I don't have one &mdash; show me an example
        </button>
      </div>

      <!--
        WHERE TO FIND IT, per wallet. The commonest reason somebody gives up here is not that they
        pasted the wrong thing; it is that they never found the right thing to paste. Collapsed,
        because a reader who already has it does not need six sets of menu directions.
      -->
      <details class="disc" style="margin-top:14px">
        <summary><span class="label">Where do I find this?</span></summary>
        <dl style="margin-top:10px">
          <div class="dr"><dt>Sparrow</dt>
            <dd>Settings tab &rarr; Policy &rarr; the "Descriptor" box. Copy the whole line.</dd></div>
          <div class="dr"><dt>Nunchuk</dt>
            <dd>Open the wallet &rarr; the &bull;&bull;&bull; menu &rarr; Wallet Configuration &rarr; Copy descriptor.</dd></div>
          <div class="dr"><dt>Coldcard</dt>
            <dd>Advanced/Tools &rarr; Export Wallet &rarr; Descriptor, saved to the microSD card.
                Open the file and copy the line beginning <code class="mono">wpkh(</code>.</dd></div>
          <div class="dr"><dt>Bitcoin Core</dt>
            <dd>Run <code class="mono">listdescriptors</code> and take the entry whose
                <code class="mono">desc</code> starts with <code class="mono">wpkh(</code>
                (the one for receiving, not the one marked internal).</dd></div>
          <div class="dr"><dt>Specter</dt>
            <dd>Wallet &rarr; Settings &rarr; Export &rarr; the descriptor is at the top.</dd></div>
        </dl>
        <p class="sub" style="margin-top:10px">
          Whatever your software calls it, you are looking for a single line beginning
          <code class="mono">wpkh(</code>, ending with a "#" and eight characters. It contains no
          private keys and cannot spend anything, which is why it is safe to paste here.
        </p>
      </details>

      <div style="margin-top:14px">
        <span class="label" style="display:block;margin-bottom:5px">Which bitcoin network</span>
        <div id="network" class="mono" data-network="${p ? p.network : ''}"
          style="width:100%;max-width:340px;padding:8px;border:1px solid var(--rule);background:var(--paper-2);font-size:12.5px;color:var(--grey)">
          ${p
            ? html`${NETWORK_PLAIN[p.network].label}
                   <span class="pill ${p.network === 'mainnet' ? 'critical' : 'info'}"
                     style="margin-left:6px">read from your wallet</span>`
            : 'read from your wallet description'}
        </div>
      </div>

      <!--
        ADVANCED, AND COLLAPSED BY DEFAULT.

        These three numbers used to sit in the main form, and they are the fields a
        non-technical reader is least able to answer. They are not cosmetic: they set how far
        this page looks when deciding whether an address is yours. Get them too low and a
        legitimate change output is reported as CHANGE_NOT_MINE — a false alarm on the
        highest-severity check, which is exactly how people learn to click through the one alert
        that matters.

        They stay reachable rather than hidden, because raising the range is precisely what
        somebody does after that false alarm, and the finding's own "what to do" text sends them
        here by name.
      -->
      <details class="disc" style="margin-top:16px">
        <summary><span class="label">Advanced: how far to look for your addresses</span></summary>
        <p class="sub" style="margin:10px 0">
          Wallets generate addresses in order and never reuse them, so this page has to know
          roughly how far along yours is. The defaults suit a wallet that has not been used
          heavily. If a payment that really is yours gets flagged as going somewhere unknown,
          raise these and load the transaction again.
        </p>
        <div class="cols-3" style="gap:12px">
          <div>
            <label class="label" for="lastUsedReceive" style="display:block;margin-bottom:5px">
              Addresses received to
            </label>
            <input id="lastUsedReceive" class="mono" type="number" min="0" value="${v.lastUsedReceive}"
              style="width:100%;padding:8px;border:1px solid var(--ink);background:var(--card);font-size:12.5px">
          </div>
          <div>
            <label class="label" for="lastUsedChange" style="display:block;margin-bottom:5px">
              Times money came back
            </label>
            <input id="lastUsedChange" class="mono" type="number" min="0" value="${v.lastUsedChange}"
              style="width:100%;padding:8px;border:1px solid var(--ink);background:var(--card);font-size:12.5px">
          </div>
          <div>
            <label class="label" for="gapLimit" style="display:block;margin-bottom:5px">
              Extra to check ahead
            </label>
            <input id="gapLimit" class="mono" type="number" min="1" value="${v.gapLimit}"
              style="width:100%;padding:8px;border:1px solid var(--ink);background:var(--card);font-size:12.5px">
          </div>
        </div>
        <p class="sub" style="margin-top:10px;font-size:12px">
          Looking further is always safe: an address can only be confirmed as yours by rebuilding
          it from your own wallet, so a wider search can never call a stranger's address yours.
          It only takes longer.
        </p>
      </details>

      <div class="row" style="margin-top:14px">
        <button class="btn" id="apply-vault">Apply</button>
        <button class="btn ghost sm" id="forget-vault">Forget this wallet</button>
        <span class="sub" style="font-size:12px">
          Stored in this browser only. A description cannot spend, but it does reveal every
          address the wallet will use.
        </span>
      </div>
    </details>`;
}

/* ── 2. load ──────────────────────────────────────────────────────────────── */

export function loadView(): Raw {
  const disabled = !state.parsed;
  const L = state.loaded;
  return html`
    <details class="card disc" id="load-card"
      style="margin-top:20px${disabled ? raw(';opacity:.5') : ''}">
      <summary>
        <h4 style="margin:0">2 · The transaction</h4>
        ${L
          ? html`<span class="pill ok" style="margin-left:8px">loaded ·
                   ${L.intent.inputs.length} in / ${L.intent.outputs.length} out</span>`
          : html`<span class="pill info" style="margin-left:8px">none</span>`}
      </summary>
      <p class="sub" style="margin:12px 0 12px">
        ${disabled
          ? 'Set up the vault above first.'
          : 'Open the transaction file someone sent you, or paste the block of text they gave you. Nothing is uploaded: the code in this page makes no network calls at all.'}
      </p>

      <div id="dropzone" style="border:2px dashed var(--rule);background:var(--paper-2);padding:22px;text-align:center;margin-bottom:12px">
        <p class="label" style="color:var(--grey);margin-bottom:9px">Drop the transaction file here</p>
        <input type="file" id="file" accept=".psbt,.txt,application/octet-stream" ${disabled ? raw('disabled') : ''}
          style="font-family:var(--label);font-size:11px">
      </div>

      <label class="label" for="paste" style="display:block;margin-bottom:6px">…or paste the text they sent you</label>
      <textarea id="paste" class="mono" rows="3" spellcheck="false" ${disabled ? raw('disabled') : ''}
        placeholder="cHNidP8BAH0CAAAAAe..."
        style="width:100%;font-size:12px;padding:10px;border:1px solid var(--ink);background:var(--card);resize:vertical"></textarea>

      <div class="row" style="margin-top:12px">
        <button class="btn" id="inspect" ${disabled ? raw('disabled') : ''}>Check it</button>
        <button class="btn ghost sm" data-scan="psbt" ${disabled ? raw('disabled') : ''}>Scan a QR</button>
        ${state.loaded ? html`<button class="btn ghost sm" id="clear">Clear</button>` : ''}
      </div>

      ${state.loadError
        ? html`<div class="note r" style="margin-top:14px">
                 <div class="h">Could not read that</div>
                 <p class="mono" style="font-size:12.5px">${state.loadError}</p>
               </div>`
        : ''}
    </details>`;
}

/* ── 3. review ────────────────────────────────────────────────────────────── */

export function reviewView(opened: readonly string[] = []): Raw {
  const L = state.loaded;
  if (!L) return raw('');

  const ordered: Severity[] = ['BLOCK', 'CRITICAL', 'WARN', 'UNVERIFIABLE', 'INFO', 'PASS'];
  const sorted = [...L.findings].sort(
    (a, b) => ordered.indexOf(a.severity) - ordered.indexOf(b.severity));
  return html`
    <section id="review" style="margin-top:20px">
      ${reviewPanel(L.intent, L.findings, {
        acknowledged: state.acknowledged,
        mustAcknowledge: L.mustAcknowledge.map((f) => f.code),
        opened,
      })}

      ${uncheckedNote(L.findings)}
    </section>`;
}

/** True when the user may move on to signing. Slice 2b consumes this. */
export const readyToSign = (): boolean =>
  !!state.loaded && !state.loaded.blocked && allAcknowledged();


/**
 * The COUNT is derived from the findings, never written down. A note headed "Two things this
 * page could not check" sat above four "Not checked" cards on an ordinary single-key payment —
 * the two the sentence named plus the fee amounts the file asserted and the coins nobody could
 * confirm unspent. A note that miscounts the cards under it teaches the reader the cards are
 * decoration.
 */
const COUNT_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
function uncheckedNote(findings: readonly { severity: string }[]): Raw {
  const n = findings.filter((f) => f.severity === 'UNVERIFIABLE').length;
  if (n === 0) return raw('');
  const word = COUNT_WORDS[n] ?? String(n);
  return html`
    <div class="note" style="margin-top:20px">
      <div class="h">${word.charAt(0).toUpperCase() + word.slice(1)} thing${n === 1 ? '' : 's'} this page could not check</div>
      Each of them needs something this page does not have on purpose: live internet data, or
      the earlier transactions the file left out. It says "not checked" for each rather than
      showing you a tick. A tick that means "we did not look" is how people stop trusting the
      ticks that matter.
    </div>`;
}

/* ── page chrome ──────────────────────────────────────────────────────────── */

/** Eyebrow + headline, so the page opens like the rest of the house style. */
export function headView(): Raw {
  return html`
    <div class="blk" style="margin-bottom:18px">
      <p class="eyebrow">The other thing this page does</p>
      <h2>Read a payment before you approve it</h2>
      <p class="sub" style="margin-bottom:0">
        Proving control says the wallet is yours. This says what a particular payment out of it
        actually <em>does</em> — where the money is going and whether anything about it looks
        wrong — in plain terms, before you approve it. Same wallet, same page, nothing uploaded.
      </p>
    </div>`;
}

/**
 * What this build does and does not do, stated as a table rather than as prose.
 *
 * Every "No" here is a real absence, not a teaser: multi-party coordination and the hash-chained
 * ⛔ UPDATED 2026-08-27. This used to say the room, the record and the attestation had "no
 * interface anywhere". They do now: `apps/signer` is the page each signer uses, and it is built
 * and tested. What is missing is that the shared-wallet side is not switched on — which needs a
 * domain and two secrets rather than more code — and that no hardware has been tested.
 *
 * The distinction is worth keeping precise. "Not built" and "built but not running" are
 * different promises, and blurring them is how a roadmap turns into a claim.
 */
export function capabilityView(): Raw {
  /*
   * What THIS page does and never does. It used to be a two-column Solo/Org matrix with rows
   * marked "Built, not switched on" and a paragraph explaining what that phrase meant — the
   * transitional state, tabulated. A key holder does not need the roadmap; they need to know
   * what will happen when they press a button here, and what cannot.
   */
  const rows: readonly [string, 'yes' | 'never'][] = [
    ['Prove you control a wallet', 'yes'],
    ['Check someone else\u2019s proof of control', 'yes'],
    ['Read a payment and check it before you approve', 'yes'],
    ['Work with ordinary wallet software', 'yes'],
    ['Work with hardware wallets, by cable or QR', 'yes'],
    ['See your private key, or ask for it', 'never'],
    ['Send anything anywhere', 'never'],
  ];
  return html`
    <div style="margin-top:26px">
      <h4>What this page does</h4>
      <div class="tbl-wrap">
        <table>
          <tbody>
            ${rows.map(([what, v]) => html`
              <tr><td>${what}</td>
                  <td><span class="pill ok">${v === 'yes' ? 'Yes' : 'Never'}</span></td></tr>`)}
          </tbody>
        </table>
      </div>
    </div>`;
}

/* ── the right rail ───────────────────────────────────────────────────────── */

/**
 * Reference material that must stay beside the working column rather than scroll past it.
 *
 * The vault's policy and network are what a reader checks a CHANGE_NOT_MINE against; the two
 * hashes are what they read out to another participant. Both used to sit above the findings and
 * disappear the moment the page got long.
 */
export function railView(): Raw {
  const p = state.parsed;
  const L = state.loaded;

  const vault = p
    ? html`<div class="card">
             <h4>Your wallet</h4>
             <p class="sub" style="margin:0 0 12px">
               ${p.n === 1
                 ? 'One key controls this wallet, so one person can move the money alone.'
                 : `${p.m} of these ${p.n} keys have to approve any payment. Holding ` +
                   `${p.n === p.m ? 'every one' : `fewer than ${p.m}`} is not enough.`}
             </p>
             <dl>
               <div class="dr"><dt>Approvals needed</dt><dd>${
            p.n === 1 ? 'one, from the one key' : `${p.m} of ${p.n}`}</dd></div>
               <div class="dr"><dt>Network</dt><dd>${NETWORK_PLAIN[p.network].label}</dd></div>
               <div class="dr"><dt>${p.n === 1 ? 'The key' : `The ${p.n} keys`}</dt><dd>${raw(p.keys
                 .map((k) => `<div>${esc(k.origin?.fingerprint ?? '????????')}</div>`)
                 .join(''))}</dd></div>
             </dl>
             <details class="tech">
               <summary>Technical detail</summary>
               <dl style="margin-top:8px">
                 <div class="dr"><dt>Policy</dt><dd>${p.m}-of-${p.n} ${p.classId}</dd></div>
                 <div class="dr"><dt>Network</dt><dd>${p.network}</dd></div>
                 <div class="dr"><dt>Scan window</dt><dd>0/${state.vault.lastUsedReceive}+${
                   state.vault.gapLimit} · 1/${state.vault.lastUsedChange}+${state.vault.gapLimit}</dd></div>
                 <div class="dr"><dt>Keys</dt><dd>${raw(p.keys
                   .map((k) => `<div>${esc(k.origin?.fingerprint ?? '????????')} · ${esc(short(k.xpub, 10, 6))}</div>`)
                   .join(''))}</dd></div>
               </dl>
             </details>
           </div>`
    : raw('');

  const verdict = L ? verdictCard(L) : raw('');

  return html`<aside class="rail">${vault}${verdict}${signingPathsCard()}</aside>`;
}

/**
 * Every route a signature can take into this page — and software wallets are named first.
 *
 * The transports were always there: a signed PSBT arrives by paste, by file or by QR, and none
 * of those care what produced it. But every label said "device", "Ledger" or "air-gapped", so a
 * Sparrow or Bitcoin Core user had no way to tell this page was for them. Anything that speaks
 * BIP-174 works, and now it says so.
 */
function signingPathsCard(): Raw {
  return html`
    <div class="card">
      <h4>How your approval comes back</h4>
      <p class="sub" style="margin-bottom:10px">
        You approve in the wallet that holds the key, and the signed file comes back to this
        page. It does not matter what software or hardware you use, only that the approval
        came from this wallet's own key.
      </p>
      <dl>
        <div class="dr"><dt>Software</dt><dd style="text-align:left;font-family:var(--serif);font-size:12.5px">
          Sparrow, Bitcoin Core, Electrum, Nunchuk, Specter and most others. Save the
          transaction from step 4, approve it there, and bring the file or the text back.</dd></div>
        <div class="dr"><dt>Hardware</dt><dd style="text-align:left;font-family:var(--serif);font-size:12.5px">
          Ledger and BitBox02, plugged in by cable. ${hasWebHid()
            ? raw('<span class="pill ok">available here</span>')
            : raw('<span class="pill unver">no WebHID in this browser</span>')}</dd></div>
        <div class="dr"><dt>Air-gapped</dt><dd style="text-align:left;font-family:var(--serif);font-size:12.5px">
          Coldcard, Keystone, Passport, Jade, SeedSigner. Hold the device up to the scrolling
          QR code on screen, then hold it back up when it has finished.</dd></div>
      </dl>
      <p class="sub" style="margin-top:10px;font-size:12px">
        Your wallet's own description decides whether an approval counts. An approval from a
        key that is not one of this wallet's is reported to you and never counted, whatever
        produced it.
      </p>
    </div>`;
}

function verdictCard(L: NonNullable<typeof state.loaded>): Raw {
  const ordered: Severity[] = ['BLOCK', 'CRITICAL', 'WARN', 'UNVERIFIABLE', 'INFO', 'PASS'];
  const counts = ordered
    .map((s) => [s, L.findings.filter((f) => f.severity === s).length] as const)
    .filter(([, n]) => n > 0);
  const ackOutstanding = L.mustAcknowledge.filter((f) => !state.acknowledged.has(f.code));

  return html`
    <div class="card" id="verdict-card">
      <h4>Where this stands</h4>
      <div class="row" style="margin-bottom:11px">
        ${counts.map(([s, n]) => html`<span class="pill ${PILL[s]}">${n} ${
          SEVERITY_PLAIN[s].label.toLowerCase()}</span>`)}
      </div>
      ${L.blocked
        ? html`<div class="note r" style="margin:0 0 11px">
                 <div class="h">You cannot sign this</div>
                 Something here is serious enough that signing is switched off, and ticking a box
                 will not clear it. The transaction has to be corrected and rebuilt by whoever
                 prepared it.
               </div>`
        : ackOutstanding.length > 0
        ? html`<div class="note ${ackOutstanding.some((f) => f.severity === 'CRITICAL') ? 'r' : ''}"
                 style="margin:0 0 11px">
                 <div class="h">${ackOutstanding.length === 1
                   ? 'One thing still needs your attention'
                   : `${ackOutstanding.length} things still need your attention`}</div>
                 ${raw(ackOutstanding.map((f) => `<div>· ${esc(explain(f.code).title)}</div>`).join(''))}
                 ${ackOutstanding.some((f) => f.severity === 'CRITICAL')
                   ? html`<p style="margin-top:8px">
                            Read these before ticking anything. The summary above already takes
                            them into account, which is why the amount may not be the one you
                            were expecting.
                          </p>`
                   : ''}
               </div>`
        : html`<div class="note f" style="margin:0 0 11px">
                 <div class="h">Reviewed</div>
                 Nothing blocking, and every finding that needed acknowledging has been. What
                 could not be checked offline is listed as UNVERIFIABLE — not checked is not
                 the same as cleared.
               </div>`}

      <div class="hashline" style="margin-top:0">
        <b>payloadHash</b><br>${L.payloadHash}<br>
        <b>intentHash</b><br>${L.intentHash}
      </div>
      <p class="sub" style="margin-top:8px;font-size:12px">
        These commit to the exact bytes loaded and to the exact summary rendered. Compare them
        with every other participant: two people shown different transactions cannot produce the
        same pair.
      </p>
      ${L.signers.length > 0
        ? html`<p class="sub" style="margin-top:8px;font-size:12px">
                 Signatures from ${L.signers.length} ${L.signers.length === 1 ? 'key' : 'keys'}:
                 ${raw(L.signers.map((f) => `<code class="mono">${esc(f)}</code>`).join(' · '))}
                 ${L.finalized ? raw('<span class="pill ok" style="margin-left:6px">finalized</span>') : ''}
               </p>`
        : ''}
    </div>`;
}



/* ── 4. assemble ──────────────────────────────────────────────────────────── */

/**
 * BIP-174 roles 4, 5 and 6. Presented as SYSTEM steps, not decisions.
 *
 * Combining signatures and assembling witnesses cannot change what gets paid, so they are
 * labelled that way deliberately — the human decision already happened in step 3, and dressing
 * mechanical assembly up as another approval would teach people that clicking through it is
 * what approval feels like.
 */
export function assembleView(): Raw {
  const L = state.loaded;
  if (!L) return raw('');
  const A = state.assembly;
  const m = state.parsed?.m ?? 0;
  const n = state.parsed?.n ?? 0;
  const have = A.signers.length;
  const met = policyMet();

  if (!readyToSign()) {
    return html`
      <section class="card" id="assemble-card" style="margin-top:20px;opacity:.5">
        <h4>4 · Add the signature and finish <span class="pill info">System</span></h4>
        <p class="sub">
          ${L.blocked
            ? 'Signing is disabled while a BLOCK-severity finding stands.'
            : 'Acknowledge the outstanding findings above first.'}
        </p>
      </section>`;
  }

  return html`
    <section class="card" id="assemble-card" style="margin-top:20px">
      <h4>4 · Add the signature and finish <span class="pill info">System step</span></h4>
      <p class="sub" style="margin-bottom:14px">
        Putting the signature back into the transaction and finishing it is mechanical — it
        cannot change what gets paid. The decision was step 3.
      </p>

      <div class="track" style="margin-bottom:14px">
        <div class="p ${met ? 'done' : 'wait'}">
          <span class="av">${have}</span>
          <span>
            <span class="nm">${have} of ${m} required signatures</span>
            <span class="rl">${n}-key vault · one key, one vote</span>
          </span>
          <span class="st">${met ? 'policy met' : `${m - have} more`}</span>
        </div>
      </div>
      <div class="meter" aria-hidden="true"><i style="width:${m ? Math.min(100, have / m * 100) : 0}%"></i></div>

      ${A.contributions.length > 0
        ? html`<div class="track" style="margin-top:14px">
                 ${A.contributions.map((c) => html`
                   <div class="p done">
                     <span class="av">✓</span>
                     <span><span class="nm mono">${c.fingerprints.join(', ')}</span>
                     <span class="rl">${c.label}</span></span>
                   </div>`)}
               </div>`
        : ''}

      <!-- OUT first, then IN: a signature cannot come back until the PSBT has gone out. -->
      <div style="margin-top:16px">
        <span class="label" style="display:block;margin-bottom:6px">Send it to a signer</span>
        <div class="row">
          <button class="btn ghost sm" data-export="tosign">Download .psbt</button>
          <button class="btn ghost sm" data-export="tosign64">Copy base64</button>
          <button class="btn ghost sm" data-qr-open="loaded">Show animated QR</button>
          ${hasWebHid()
            ? html`<button class="btn ghost sm" id="ledger-connect">Connect a Ledger</button>`
            : html`<span class="pill unver" title="Firefox and Safari declined to implement WebHID">
                     No WebHID here — use a file or QR
                   </span>`}
          ${state.ledger.message
            ? html`<span class="sub" style="font-size:12px">${state.ledger.message}</span>`
            : ''}
        </div>
        <p class="sub" style="margin-top:8px;font-size:12px">
          Open the file in Sparrow, Bitcoin Core, Electrum, Nunchuk, Specter or most other
          wallet software, approve it there, and bring it back below.
        </p>
      </div>

      <div style="margin-top:16px">
        <label class="label" for="sig-paste" style="display:block;margin-bottom:6px">
          Bring the signed PSBT back
        </label>
        <textarea id="sig-paste" class="mono" rows="3" spellcheck="false"
          placeholder="cHNidP8BAH0CAAAAAe…"
          style="width:100%;font-size:12px;padding:10px;border:1px solid var(--ink);background:var(--card);resize:vertical"></textarea>
        <div class="row" style="margin-top:10px">
          <input type="file" id="sig-file" accept=".psbt,.txt,application/octet-stream"
            style="font-family:var(--label);font-size:11px">
          <button class="btn sm" id="add-sig">Add signature</button>
          <button class="btn ghost sm" data-scan="signature">Scan signed QR</button>
        </div>
        ${state.ledger.needsRegistration
          ? html`<div class="note" style="margin-top:10px">
                   <div class="h">Approve the vault on the device</div>
                   Your Ledger has not been shown this vault before. It will display the policy —
                   compare it against your descriptor, then approve. It will not sign into a
                   script it has not been shown, which is exactly the check that catches a
                   swapped descriptor.
                   <div class="row" style="margin-top:10px">
                     <button class="btn sm" id="ledger-register">Register the policy</button>
                   </div>
                 </div>`
          : ''}
        ${state.ledger.error
          ? html`<div class="note r" style="margin-top:10px">
                   <div class="h">Ledger</div>
                   <p class="mono" style="font-size:12.5px">${state.ledger.error}</p>
                 </div>`
          : ''}
        ${state.assembly.combined
          ? html`<div class="row" style="margin-top:10px">
                   <button class="btn ghost sm" data-qr-open="combined">Show combined QR</button>
                 </div>`
          : ''}
      </div>

      ${A.error
        ? html`<div class="note r" style="margin-top:14px">
                 <div class="h">Refused</div>
                 <p class="mono" style="font-size:12.5px">${A.error}</p>
               </div>`
        : ''}

      ${A.combined
        ? html`<div style="margin-top:18px">
                 <div class="rule"></div>
                 <h4>5 · Finalize <span class="pill info">System step</span></h4>
                 ${A.finalized
                   ? html`<div class="note f">
                            <div class="h">Witnesses assembled</div>
                            Every input is final. Nothing further can be signed.
                          </div>`
                   : met
                   ? html`<p class="sub" style="margin-bottom:10px">
                            ${have} signatures collected — enough to satisfy ${m}-of-${n}.
                          </p>
                          <button class="btn" id="finalize">Finalize</button>`
                   : html`<p class="sub">
                            Needs ${m} signatures, has ${have}. Finalizing now would fail —
                            the collected signatures do not satisfy the script.
                          </p>`}
               </div>`
        : ''}

      ${A.extracted
        ? html`<div style="margin-top:18px">
                 <div class="rule"></div>
                 <h4>6 · The transaction <span class="pill info">System step</span></h4>
                 <dl>
                   <div class="dr"><dt>txid</dt><dd>${A.extracted.txid}</dd></div>
                   <div class="dr"><dt>size</dt><dd>${A.extracted.vsize} vB</dd></div>
                   <div class="dr"><dt>fee rate</dt><dd>${
                     L.intent.feeSats !== null
                       ? `${(Number(L.intent.feeSats) / A.extracted.vsize).toFixed(2)} sat/vB (actual)`
                       : 'unknown'}</dd></div>
                 </dl>
                 <div class="note" style="margin-top:14px">
                   <div class="h">Broadcasting is a separate act</div>
                   Solo has no network access, so it cannot broadcast and does not try. Export the
                   raw transaction and push it from wherever you choose — that keeps the decision
                   to publish explicitly yours.
                 </div>
                 <div class="row" style="margin-top:14px">
                   <button class="btn" data-export="rawhex">Copy raw hex</button>
                   <button class="btn ghost sm" data-export="rawfile">Download .txn</button>
                   <button class="btn ghost sm" data-export="psbt">Download final .psbt</button>
                 </div>
               </div>`
        : A.combined
        ? html`<div class="row" style="margin-top:14px">
                 <button class="btn ghost sm" data-export="combined">Download combined .psbt</button>
                 <button class="btn ghost sm" data-export="combined64">Copy combined base64</button>
               </div>`
        : ''}
    </section>`;
}


/* ── QR panels ────────────────────────────────────────────────────────────── */

/**
 * The QR frames are rendered by main.ts directly into `#qr-frame`, NOT here.
 *
 * An animated QR advances several times a second. Re-rendering the whole app at that rate would
 * fight every other interaction on the page — you could not tick an acknowledgement while a QR
 * was playing. So this emits the chrome once and the animation owns one node inside it.
 */
export function qrOutView(): Raw {
  const L = state.loaded;
  if (!L) return raw('');
  const Q = state.qr;
  if (!Q.open) return raw('');

  return html`
    <section class="card" id="qr-out" style="margin-top:20px">
      <h4>Show this to your signing device</h4>
      <div class="row" style="margin-bottom:12px">
        <div class="modesw">
          <button data-qr-format="ur" aria-pressed="${String(Q.format === 'ur')}">ur:crypto-psbt</button>
          <button data-qr-format="bbqr" aria-pressed="${String(Q.format === 'bbqr')}">BBQr</button>
        </div>
        <span class="sub" style="font-size:12px">
          ${Q.format === 'ur'
            ? 'Keystone · Passport · Jade · Sparrow · Nunchuk'
            : 'Coldcard'}
        </span>
      </div>

      <div style="display:flex;gap:18px;flex-wrap:wrap;align-items:flex-start">
        <div id="qr-frame" style="width:300px;height:300px;background:var(--card);border:1px solid var(--ink);padding:10px"></div>
        <div style="flex:1;min-width:200px">
          <dl>
            <div class="dr"><dt>Frames</dt><dd id="qr-count">${Q.frames.length}</dd></div>
            <div class="dr"><dt>Showing</dt><dd id="qr-index">1</dd></div>
            <div class="dr"><dt>Source</dt><dd>${Q.source}</dd></div>
          </dl>
          <div class="row" style="margin-top:12px">
            <button class="btn ghost sm" id="qr-slower">Slower</button>
            <button class="btn ghost sm" id="qr-faster">Faster</button>
            <button class="btn ghost sm" id="qr-close">Close</button>
          </div>
          <p class="sub" style="margin-top:10px;font-size:12px">
            If your device cannot lock on, try Slower — small fixed-focus cameras need longer on
            each frame than a phone does.
          </p>
        </div>
      </div>
    </section>`;
}
